import RequirePricingAdminUser from '@/components/auth/RequirePricingAdminUser';
import DateTimeDisplay from '@/components/DateTime';
import { formatITCModifierData } from '@/components/Pricing/ITCModifiers/SpecificITCModifier';
import {
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ItcModifierSystemType, PriceSheetStatus, ProgramType } from 'types';

import {
  useDeleteOrganizationEnergyCommunityMutation,
  useGetOrganizationEnergyCommunityQuery,
} from '../../../../../services/pricing';
import { TableLoading } from '../../../../TableLoading';

import { CreateEnergyCommunity } from './modals/CreateEnergyCommunity';

export const EnergyCommunity = () => {
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const {
    data = [],
    isLoading,
    isFetching,
  } = useGetOrganizationEnergyCommunityQuery({ alias, programType: programType }, { skip: !alias || !programType });
  const [deleteOrganizationEnergyCommunityMutation] = useDeleteOrganizationEnergyCommunityMutation();
  const energyCommunities = useMemo(() => {
    if (!data || !data?.energyCommunities?.length) {
      return [];
    }
    const ecByStatus = data.energyCommunities.reduce((acc: any, ec: any) => {
      acc[ec.status] = acc[ec.status] || [];
      acc[ec.status].push(ec);
      return acc;
    }, {});
    const sortByCreatedDateDesc = (a: any, b: any) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();

    return [
      ...(ecByStatus[PriceSheetStatus.active] || []).toSorted(sortByCreatedDateDesc),
      ...(ecByStatus[PriceSheetStatus.archived] || []).toSorted(sortByCreatedDateDesc),
    ];
  }, [data]);
  const handleArchive = async (id: string) => {
    try {
      await deleteOrganizationEnergyCommunityMutation({ alias, id, programType }).unwrap();
      toast.success('Energy Community Archived successfully');
    } catch (e) {
      console.error(e);
      toast.error('Error archiving Energy Community');
    }
  };

  const sharedColumnConfig: Column[] = [
    {
      heading: 'status',
      dataKey: 'status',
      render: (cell: any) => (
        <Badge
          variant={
            cell === PriceSheetStatus.active ? 'success' : cell === PriceSheetStatus.archived ? 'warning' : 'danger'
          }
          message={cell}
        />
      ),
    },
    { heading: 'Name', dataKey: 'name' },
    { heading: 'org', dataKey: 'organizationName' },
    { heading: 'State', dataKey: 'state' },
  ];

  const geothermalColumnConfig: Column[] = [
    ...sharedColumnConfig,
    {
      heading: 'Coal Closure',
      render: (_cell: any, row: any) => formatITCModifierData(row.values[0]?.coalTract),
    },
    {
      heading: 'Fossil Fuel Employment',
      render: (_cell: any, row: any) => formatITCModifierData(row.values[0]?.fossilFuel),
    },
    {
      heading: 'Both',
      render: (_cell: any, row: any) => formatITCModifierData(row.values[0]?.combined),
    },
  ];

  const solarColumnConfig: Column[] = [
    ...sharedColumnConfig,
    {
      heading: 'PV Only',
      render: (_cell: any, row: any) => {
        const pv = row.values.find((d: any) => d.systemType === ItcModifierSystemType.pvOnly);
        if (!pv || !pv?.coalTract) return '';
        return (
          <Box childGap="sm">
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Coal Closure:</Box> {formatITCModifierData(pv.coalTract)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Fossil Fuel: </Box>
              {formatITCModifierData(pv.fossilFuel)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Both:</Box> {formatITCModifierData(pv.combined)}
            </Box>
          </Box>
        );
      },
    },
    {
      heading: 'Storage Only',
      render: (_cell: any, row: any) => {
        const st = row.values.find((d: any) => d.systemType === ItcModifierSystemType.storageOnly);
        if (!st || !st?.coalTract) return '';
        return (
          <Box childGap="sm">
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Coal Closure:</Box> {formatITCModifierData(st.coalTract)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Fossil Fuel: </Box>
              {formatITCModifierData(st.fossilFuel)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Both:</Box> {formatITCModifierData(st.combined)}
            </Box>
          </Box>
        );
      },
    },
    {
      heading: 'PV and Storage',
      render: (_cell: any, row: any) => {
        const pvStor = row.values.find((d: any) => d.systemType === ItcModifierSystemType.pvAndStorage);
        if (!pvStor || !pvStor?.coalTract) return '';

        return (
          <Box childGap="sm">
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Coal Closure:</Box> {formatITCModifierData(pvStor.coalTract)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Fossil Fuel: </Box>
              {formatITCModifierData(pvStor.fossilFuel)}
            </Box>
            <Box direction="row" childGap="xs">
              <Box fontWeight="bold">Both:</Box> {formatITCModifierData(pvStor.combined)}
            </Box>
          </Box>
        );
      },
    },
  ];

  const actionColumnConfig: Column[] = [
    {
      heading: 'Created',
      dataKey: 'createdDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.createdDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.archivedDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        return (
          row.status === PriceSheetStatus.active && (
            <RequirePricingAdminUser>
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                onClick={() => {
                  handleArchive(row.id);
                }}
              >
                Archive
              </Button>
            </RequirePricingAdminUser>
          )
        );
      },
    },
  ];
  const columnConfig =
    programType === ProgramType.geothermal
      ? [...geothermalColumnConfig, ...actionColumnConfig]
      : [...solarColumnConfig, ...actionColumnConfig];

  return (
    <>
      <CreateEnergyCommunity isModalOpen={isModalOpen} closeModal={closeModal} />
      <Card.Header>
        <RequirePricingAdminUser>
          <Box direction="row" justifyContent="space-between">
            <Box />
            <Button
              as="button"
              variant="primary"
              iconPrefix="add"
              size={{ base: 'sm', desktop: 'md' }}
              aria-label="create adder"
              onClick={() => openModal()}
            >
              {!isPhone && 'Energy Community'}
            </Button>
          </Box>
        </RequirePricingAdminUser>
      </Card.Header>
      <Box margin="0 lg 0 lg">
        {isFetching || isLoading ? (
          <TableLoading rows={12} />
        ) : (
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={energyCommunities}
            isLoading={isLoading || isFetching}
            isScrollable={{ y: true }}
            hasStickyHeader
          />
        )}
      </Box>
    </>
  );
};
